import { isFile, isHtmlString, isRemoteValue } from '@/modules/outputs/utils';
import {
  EOutputCardVariant,
  type TResolvedFile,
  type TResolvedRemoteValue,
  type TResolvedValue,
} from '@/modules/outputs/types';
import {
  getOutputCardSummary,
  getOutputCardVariant,
  getOutputSourceText,
  isOutputCardClipped,
  getOutputTablePreview,
  isPdfFile,
} from './output-card-utils';

export interface IOutputPresentation {
  variant: EOutputCardVariant;
  summary: string;
  clipped: boolean;
  loading: boolean;
  copyable: boolean;
  downloadable: boolean;
  expandable: boolean;
  compactInAssistant: boolean;
  defaultColumnWidth: number;
}

export type TOutputModalLayout = 'centered' | 'wide' | 'idp';
/**
 * `fit` shrink-wraps the shell around whatever the renderer draws, bounded by
 * the same ceiling as `regular`. Every other size pins a height up front.
 */
export type TOutputModalSize = 'compact' | 'fit' | 'regular' | 'large';

/**
 * `pane` gets the bordered, tinted content surface (file list, document,
 * IDP); `plain` gets none of that chrome and lets its own sheet fill the
 * body (text, JSON, table, image, browser, and everything else).
 */
export type TOutputModalContentMode = 'pane' | 'plain';

/**
 * Shared modal decisions used by every output surface. The renderer remains
 * responsible for the actual value; this adapter only decides how much room
 * the value needs and which surrounding layout best supports inspection.
 */
export interface IOutputModalPresentation extends IOutputPresentation {
  layout: TOutputModalLayout;
  size: TOutputModalSize;
  contentMode: TOutputModalContentMode;
  /**
   * Whether the modal offers zoom beside its other actions. Only a renderer
   * that draws at a scale can use it, so each output type opts in.
   */
  zoomable: boolean;
}

const COLUMN_WIDTH_BY_VARIANT: Record<EOutputCardVariant, number> = {
  [EOutputCardVariant.NUMBER]: 128,
  [EOutputCardVariant.EMPTY]: 128,
  [EOutputCardVariant.TEXT]: 200,
  [EOutputCardVariant.CODE]: 220,
  [EOutputCardVariant.DICT]: 220,
  [EOutputCardVariant.LIST]: 220,
  [EOutputCardVariant.FILE]: 220,
  [EOutputCardVariant.TABLE]: 240,
  [EOutputCardVariant.IDP]: 240,
  [EOutputCardVariant.BROWSER]: 240,
};

const DOWNLOADABLE_VARIANTS = new Set<EOutputCardVariant>([
  EOutputCardVariant.LIST,
  EOutputCardVariant.TABLE,
  EOutputCardVariant.IDP,
  EOutputCardVariant.FILE,
  EOutputCardVariant.CODE,
  EOutputCardVariant.DICT,
]);

const COPYABLE_VARIANTS = new Set<EOutputCardVariant>([
  EOutputCardVariant.TEXT,
  EOutputCardVariant.NUMBER,
  EOutputCardVariant.TABLE,
  EOutputCardVariant.CODE,
  EOutputCardVariant.DICT,
]);

function getRemoteVariant(value: TResolvedRemoteValue): EOutputCardVariant {
  switch (value.kind) {
    case 'text':
      return EOutputCardVariant.TEXT;
    case 'list':
    case 'set':
      return EOutputCardVariant.TABLE;
    case 'dictionary':
      return EOutputCardVariant.DICT;
  }
}

/** Shared, surface-independent facts derived from one resolved output value. */
export function getOutputPresentation(
  value: TResolvedValue,
): IOutputPresentation {
  const loading = isRemoteValue(value);
  const variant = loading
    ? getRemoteVariant(value)
    : getOutputCardVariant(value);
  const summary = getOutputCardSummary(value, variant);
  const compactInAssistant =
    variant === EOutputCardVariant.NUMBER ||
    variant === EOutputCardVariant.EMPTY ||
    (variant === EOutputCardVariant.TEXT &&
      !loading &&
      String(value).length <= 120);

  return {
    variant,
    summary,
    clipped: loading ? false : isOutputCardClipped(value, variant),
    loading,
    copyable: !loading && COPYABLE_VARIANTS.has(variant),
    downloadable: !loading && DOWNLOADABLE_VARIANTS.has(variant),
    expandable: value !== null && !loading,
    compactInAssistant,
    defaultColumnWidth: COLUMN_WIDTH_BY_VARIANT[variant],
  };
}

function serializedLength(value: TResolvedValue): number {
  try {
    return JSON.stringify(value).length;
  } catch {
    return String(value).length;
  }
}

function lineCount(value: string): number {
  return value.split(/\r?\n/).length;
}

/**
 * Row count up to which a table output shrink-wraps to its content. Past it
 * the grid keeps its own scroll, so the shell needs a fixed height to scroll
 * inside. `OUTPUT_MODAL_TABLE_OPTIONS` hands this same limit to the table, so
 * the modal and the grid can't disagree about which one is scrolling.
 */
export const TABLE_FIT_ROW_LIMIT = 20;

/** Text at or under this size opens in a small, centred modal rather than a
 *  full-height one — roughly a short paragraph. */
const COMPACT_TEXT_MAX_CHARS = 240;
const COMPACT_TEXT_MAX_LINES = 3;

/**
 * Whether the modal wraps this output in the bordered, tinted pane surface
 * or leaves it plain so the renderer's own sheet fills the body.
 *
 * Pane is reserved for the file list (its own sidebar browser), a PDF
 * (`PdfRenderer`'s viewer mode already paints its page sheet assuming this
 * surface sits behind it), IDP, and the loading placeholder — matching the
 * design spec's "pane mode: file list, document, IDP" rule. A number or
 * empty result used to keep pane too, as a workaround for `PrimitiveRenderer`
 * drawing no sheet of its own; now that it draws its own `bg-card` sheet in
 * expanded mode, it no longer needs the extra pane behind it. Everything
 * else — including an image, which the modal gives a deliberate full-bleed
 * frame instead — is plain.
 */
function getOutputModalContentMode(
  base: IOutputPresentation,
  value: TResolvedValue,
): TOutputModalContentMode {
  if (base.loading) return 'pane';

  if (
    base.variant === EOutputCardVariant.LIST ||
    base.variant === EOutputCardVariant.IDP
  ) {
    return 'pane';
  }

  if (base.variant === EOutputCardVariant.FILE) {
    return isPdfFile(value as TResolvedFile) ? 'pane' : 'plain';
  }

  return 'plain';
}

/**
 * Derives an adaptive modal shape from the resolved value. Values that need
 * no more than their own text open compact and centred; anything with its own
 * renderer falls through to the wide default until that renderer says
 * otherwise.
 */
export function getOutputModalPresentation(
  value: TResolvedValue,
): IOutputModalPresentation {
  const base = getOutputPresentation(value);
  const { variant } = base;
  const contentMode = getOutputModalContentMode(base, value);

  if (base.loading) {
    return {
      ...base,
      contentMode,
      zoomable: false,
      layout: 'centered',
      size: 'compact',
    };
  }

  if (
    variant === EOutputCardVariant.NUMBER ||
    variant === EOutputCardVariant.EMPTY
  ) {
    return {
      ...base,
      contentMode,
      zoomable: false,
      layout: 'centered',
      size: 'compact',
    };
  }

  if (variant === EOutputCardVariant.TEXT) {
    return {
      ...base,
      contentMode,
      zoomable: false,
      layout: 'centered',
      size: String(value).length <= 120 ? 'compact' : 'regular',
    };
  }

  if (variant === EOutputCardVariant.TABLE) {
    const table = getOutputTablePreview(value);
    // A remote table's shape is unknown until fetched, so it gets the fixed viewer, not the shrink-wrap a small table gets.
    const rowsKnown = table.headers.length > 0;
    return {
      ...base,
      contentMode,
      zoomable: false,
      layout: 'wide',
      size: !rowsKnown
        ? 'regular'
        : table.totalRows > TABLE_FIT_ROW_LIMIT
          ? 'large'
          : 'fit',
    };
  }

  // The document beside its fields needs the room a PDF gets.
  if (variant === EOutputCardVariant.IDP) {
    return {
      ...base,
      contentMode,
      zoomable: false,
      layout: 'idp',
      size: 'large',
    };
  }

  if (variant === EOutputCardVariant.FILE) {
    // `pdfZoom` is read by `PdfRenderer` alone; an image, a text file and the
    // docx/xlsx paths all ignore it, so only a PDF gets the control.
    return {
      ...base,
      contentMode,
      zoomable: isFile(value) && isPdfFile(value),
      layout: 'wide',
      size: 'large',
    };
  }

  // Keeps the document viewer's footprint so switching between files doesn't resize the modal under the cursor.
  if (variant === EOutputCardVariant.LIST) {
    return {
      ...base,
      contentMode,
      zoomable: false,
      layout: 'wide',
      size: 'large',
    };
  }

  if (variant === EOutputCardVariant.CODE) {
    // The document itself, not the card's one-line summary — sizing the
    // modal by the label would open every read-content result compact.
    const text = getOutputSourceText(value);
    if (isHtmlString(value)) {
      return {
        ...base,
        contentMode,
        zoomable: false,
        layout: 'wide',
        size: 'large',
      };
    }
    const compact =
      text.length <= COMPACT_TEXT_MAX_CHARS &&
      lineCount(text) <= COMPACT_TEXT_MAX_LINES;
    return {
      ...base,
      contentMode,
      zoomable: false,
      layout: compact ? 'centered' : 'wide',
      size: compact ? 'compact' : 'regular',
    };
  }

  if (variant === EOutputCardVariant.DICT) {
    const compact = serializedLength(value) <= 180;
    return {
      ...base,
      contentMode,
      zoomable: false,
      layout: compact ? 'centered' : 'wide',
      size: compact ? 'compact' : 'regular',
    };
  }

  return {
    ...base,
    contentMode,
    zoomable: false,
    layout: 'wide',
    size: 'regular',
  };
}
